import { Plus, Search, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { apiUrl } from '@/config/api';

interface DepartmentClassification {
  id: string; // Generated on backend
  majorCode: string;
  majorName: string;
  middleCode: string;
  middleName: string;
  detailCode: string;
  detailName: string;
}

export default function DepartmentClassificationList() {
  const [classifications, setClassifications] = useState<DepartmentClassification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<DepartmentClassification>>({});

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(apiUrl('/api/master/classifications'));
      if (res.ok) {
        const data = (await res.json()) as DepartmentClassification[];
        setClassifications(data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this classification?')) return;
    try {
      const res = await fetch(apiUrl(`/api/master/classifications/${id}`), {
        method: 'DELETE',
      });
      if (res.ok) {
        fetchData();
      } else {
        alert('Failed to delete');
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch(apiUrl('/api/master/classifications'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (res.ok) {
        setIsModalOpen(false);
        setFormData({});
        fetchData();
      } else {
        alert('Failed to save');
      }
    } catch (e) {
      console.error(e);
    }
  };

  const filtered = classifications.filter((c) =>
    Object.values(c).some((v) => String(v).toLowerCase().includes(searchTerm.toLowerCase())),
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">学部系統マスタ (Faculty Fields)</h1>
          <p className="text-gray-500 mt-1">Manage MEXT Department Classifications</p>
        </div>
        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition"
        >
          <Plus className="w-4 h-4" />
          Add New
        </button>
      </div>

      <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-sm">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search by code or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
          />
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
        <table className="w-full text-sm text-left">
          <thead className="bg-gray-50 text-gray-500 uppercase font-medium border-b border-gray-200 text-xs">
            <tr>
              <th className="px-6 py-3">Major</th>
              <th className="px-6 py-3">Middle</th>
              <th className="px-6 py-3">Detail (Minor)</th>
              <th className="px-6 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan={4} className="p-8 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={4} className="p-8 text-center text-gray-500">
                  No data found
                </td>
              </tr>
            ) : (
              filtered.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <span className="font-mono text-gray-400 mr-2">{item.majorCode}</span>
                    <span className="font-medium text-gray-900">{item.majorName}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-mono text-gray-400 mr-2">{item.middleCode}</span>
                    <span>{item.middleName}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-mono text-gray-400 mr-2">{item.detailCode}</span>
                    <span>{item.detailName}</span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      type="button"
                      onClick={() => handleDelete(item.id)}
                      className="text-red-500 hover:text-red-700 p-1 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
              <h2 className="font-semibold text-lg">Add Classification</h2>
              <button type="button" onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="majorCode" className="block text-xs font-medium text-gray-700 mb-1">
                    Major Code
                  </label>
                  <input
                    id="majorCode"
                    required
                    className="w-full border rounded p-2 text-sm"
                    value={formData.majorCode || ''}
                    onChange={(e) => setFormData({ ...formData, majorCode: e.target.value })}
                  />
                </div>
                <div>
                  <label htmlFor="majorName" className="block text-xs font-medium text-gray-700 mb-1">
                    Major Name
                  </label>
                  <input
                    id="majorName"
                    required
                    className="w-full border rounded p-2 text-sm"
                    value={formData.majorName || ''}
                    onChange={(e) => setFormData({ ...formData, majorName: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="middleCode" className="block text-xs font-medium text-gray-700 mb-1">
                    Middle Code
                  </label>
                  <input
                    id="middleCode"
                    required
                    className="w-full border rounded p-2 text-sm"
                    value={formData.middleCode || ''}
                    onChange={(e) => setFormData({ ...formData, middleCode: e.target.value })}
                  />
                </div>
                <div>
                  <label htmlFor="middleName" className="block text-xs font-medium text-gray-700 mb-1">
                    Middle Name
                  </label>
                  <input
                    id="middleName"
                    required
                    className="w-full border rounded p-2 text-sm"
                    value={formData.middleName || ''}
                    onChange={(e) => setFormData({ ...formData, middleName: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="detailCode" className="block text-xs font-medium text-gray-700 mb-1">
                    Detail Code
                  </label>
                  <input
                    id="detailCode"
                    required
                    className="w-full border rounded p-2 text-sm"
                    value={formData.detailCode || ''}
                    onChange={(e) => setFormData({ ...formData, detailCode: e.target.value })}
                  />
                </div>
                <div>
                  <label htmlFor="detailName" className="block text-xs font-medium text-gray-700 mb-1">
                    Detail Name
                  </label>
                  <input
                    id="detailName"
                    required
                    className="w-full border rounded p-2 text-sm"
                    value={formData.detailName || ''}
                    onChange={(e) => setFormData({ ...formData, detailName: e.target.value })}
                  />
                </div>
              </div>
              <div className="pt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm"
                >
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
